import type { UploadFile } from 'antd/lib/upload/interface';
import type {
  InventoryLocation,
  InventoryLocationBuilding,
  InventoryLocationBuildingFloorplan,
  SerializedInventoryLocationBuildingFloorplan,
  SerializedInventoryMeter,
} from '@enmon/commons/lib/tools';
import { buildQuery } from '@enmon/commons/lib/tools/utils/buildQuery';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import type { BuildingFloor } from '@enmon/commons';
import type { CountableApiResponse, SerializedCombinedMeter } from '@enmon/commons/lib/enmon';
import { BaseApiClient } from './baseApiClient';

export interface FindInventoryLocationBuildingFloorplanArgs {
  filter: FloorplanFilter;
  signal?: AbortSignal;
}

export type FindInventoryLocationBuildingFloorplanResponse =
  StrapiApiResponse<SerializedInventoryLocationBuildingFloorplan>;

export type CreateFloorplanArg = CreateFloorplanDTO;
export type CreateFloorplanResult = InventoryLocationBuildingFloorplan;

export type DeleteFloorplanArg = InventoryLocationBuildingFloorplan['id'];
export type DeleteFloorplanResult = void;

export interface CreateFloorplanDTO extends Pick<InventoryLocationBuildingFloorplan, 'floor' | 'inv_loc_building'> {
  floorplanFiles: UploadFile[];
  inv_location?: InventoryLocation['id'];
}

export interface FloorplanFilter {
  floor: BuildingFloor;
  inventory_location_building_id: InventoryLocationBuilding['id'];
  inventory_location_id: InventoryLocation['id'];
}

export interface FloorplanTableFilter {
  inv_loc_building?: InventoryLocationBuilding['id'][];
  sort?: string;
}

export type FloorplanMetersFilter = Partial<Pick<SerializedInventoryMeter, 'id'>>;

export type GetFloorplanMetersArg = {
  floorplanId: InventoryLocationBuildingFloorplan['id'];
  filter: FloorplanMetersFilter;
};

export type GetFloorplanMetersResult = CountableApiResponse<SerializedCombinedMeter[]>;

export class BuildingFloorplanApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('inventory-location-building-floorplans', undefined, jwt);
  }

  public async create(values: CreateFloorplanDTO): Promise<InventoryLocationBuildingFloorplan> {
    const { floorplanFiles, ...data } = values;

    const formData = new FormData();
    formData.append('files.floorplan', floorplanFiles[0]?.originFileObj);
    const JSONForm = JSON.stringify(data);
    formData.append('data', JSONForm);

    return await this.axios.post('', formData);
  }

  public async find({
    filter,
    signal,
  }: FindInventoryLocationBuildingFloorplanArgs): Promise<FindInventoryLocationBuildingFloorplanResponse> {
    const response = await this.axios.get<FindInventoryLocationBuildingFloorplanResponse>('', {
      params: buildQuery<InventoryLocationBuildingFloorplan>({
        filters: {
          floor: { $eq: filter.floor },
          ...(filter.inventory_location_building_id
            ? { inv_loc_building: { id: { $eq: filter.inventory_location_building_id } } }
            : { inv_location: { id: { $eq: filter.inventory_location_id } } }),
        },
        populate: ['inv_loc_building'],
      }),
      signal,
    });

    return response.data;
  }

  public async getMeters(
    { floorplanId, filter }: GetFloorplanMetersArg,
    signal?: AbortSignal,
  ): Promise<GetFloorplanMetersResult> {
    const response = await this.axios.get<GetFloorplanMetersResult>(`${floorplanId}/meters`, {
      params: buildQuery<SerializedInventoryMeter>({
        filters: {
          ...(filter.id && { id: { $in: [filter.id] } }),
        },
      }),
      signal,
    });

    return response.data;
  }

  public async getContent(id: InventoryLocationBuildingFloorplan['id'], signal?: AbortSignal): Promise<Blob> {
    const response = await this.axios.get<Blob>(`${id}/content`, {
      responseType: 'blob',
      signal,
    });

    return response.data;
  }

  public async delete(id: DeleteFloorplanArg): Promise<DeleteFloorplanResult> {
    await this.axios.delete(id.toString());
  }
}
